import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

/**
 * A held file opened from its chip: photos, PDFs, recordings and video play
 * in place; a CSV or text file shows its text. Nothing is uploaded, the file
 * is still only in this browser. A spreadsheet has no preview, so it offers
 * the file itself.
 */
export default function FilePreviewDialog({ file, onClose }: { file: File | null; onClose: () => void }) {
    const [url, setUrl] = useState<string | null>(null)
    const [text, setText] = useState<string | null>(null)

    useEffect(() => {
        if (!file) return
        const next = URL.createObjectURL(file)
        setUrl(next)
        setText(null)
        if (/^text\/|csv/.test(file.type) || /\.(csv|txt)$/i.test(file.name)) {
            file.text().then((t) => setText(t.slice(0, 50000))).catch(() => setText(''))
        }
        return () => URL.revokeObjectURL(next)
    }, [file])

    if (!file) return <Dialog open={false} />
    const type = file.type
    const isPdf = type === 'application/pdf' || /\.pdf$/i.test(file.name)

    return (
        <Dialog open onOpenChange={(next) => !next && onClose()}>
            <DialogContent className='flex max-h-[92dvh] w-[calc(100%-1rem)] max-w-4xl flex-col gap-3 p-4 sm:max-w-4xl'>
                <div className='pr-8'>
                    <DialogTitle className='truncate text-sm font-medium'>{file.name}</DialogTitle>
                    <DialogDescription className='text-xs'>{(file.size / 1024 / 1024).toFixed(1)} MB</DialogDescription>
                </div>
                <div className='flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg bg-gray-50'>
                    {!url ? null : type.startsWith('image/') ? (
                        <img src={url} alt={file.name} className='max-h-[75dvh] w-auto object-contain' />
                    ) : isPdf ? (
                        <iframe src={url} title={file.name} className='h-[75dvh] w-full rounded-lg bg-white' />
                    ) : type.startsWith('audio/') ? (
                        <audio src={url} controls autoPlay className='w-full max-w-md p-6' />
                    ) : type.startsWith('video/') ? (
                        <video src={url} controls className='max-h-[75dvh] w-full' />
                    ) : text !== null ? (
                        <pre className='h-[60dvh] w-full overflow-auto whitespace-pre-wrap p-4 text-xs'>{text}</pre>
                    ) : (
                        <div className='flex flex-col items-center gap-3 p-10 text-sm text-muted-foreground'>
                            There is no preview for this kind of file.
                            <Button asChild variant='outline' size='sm'>
                                <a href={url} download={file.name}>
                                    <Download className='h-4 w-4' /> Download
                                </a>
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
